"use client";

import type { RefObject } from "react";

import { useCallback, useEffect, useRef, useState } from "react";

// isTakeoverOpen and isEnteringTakeover both derive from this so they can't
// drift out of sync with each other.
const TAKEOVER_OPEN_STEP_INDEX = 1;

type UseLeadFormTakeoverOptions = {
  /** Cancels an in-flight step transition — Close should win immediately. */
  cancelInFlightTransition: () => void;
  currentStepIndex: number;
  /** fullscreenTakeover.enabled from the Sanity leadForm document. */
  enabled: boolean;
  /** Unwinds this form's browser history back to step 1. */
  goToFirstStepHistory: () => void;
};

type UseLeadFormTakeoverReturn = {
  /** Passed as `ref` on the lead `<form>` element. */
  captureFormElement: (element: HTMLFormElement | null) => void;
  /** The lead `<form>` element, used as the takeover Dialog's portal container. */
  formElement: HTMLFormElement | null;
  /** Focus target restored when the takeover closes. */
  inlineStepRegionRef: RefObject<HTMLDivElement | null>;
  isEnteringTakeover: boolean;
  isTakeoverOpen: boolean;
  onCloseTakeover: () => void;
};

/**
 * Owns the fullscreen takeover's derived open state, the `<form>` element
 * capture for the Dialog's portal container, closing back to step 1, and
 * the focus-restore effect when the takeover closes.
 */
function useLeadFormTakeover({
  cancelInFlightTransition,
  currentStepIndex,
  enabled,
  goToFirstStepHistory,
}: UseLeadFormTakeoverOptions): UseLeadFormTakeoverReturn {
  // State, not useRef: the takeover Dialog's `container` prop needs this
  // element during render, and reading ref.current there is a lint error.
  const [formElement, captureFormElement] = useState<HTMLFormElement | null>(
    null,
  );

  const inlineStepRegionRef = useRef<HTMLDivElement>(null);
  const wasTakeoverOpenRef = useRef(false);

  // Derived, never stored: currentStepIndex is already the source of truth
  // (synced with browser history), so a separate useState(isOpen) would risk
  // desyncing from browser Back.
  const isTakeoverOpen =
    enabled && currentStepIndex >= TAKEOVER_OPEN_STEP_INDEX;

  // True only for the single advance that opens the takeover (step 1 -> 2),
  // so its own open animation doesn't compete with a CMS stepTransition.
  const isEnteringTakeover =
    enabled && currentStepIndex === TAKEOVER_OPEN_STEP_INDEX - 1;

  // Closing is navigation, not new state: unwinding history back to step 1
  // closes the dialog by construction once popstate resyncs the machine.
  // Unlike Back, this cancels rather than defers to an in-flight
  // transition — Close should win immediately, with no deferred advance to race.
  const onCloseTakeover = useCallback(() => {
    cancelInFlightTransition();
    goToFirstStepHistory();
  }, [cancelInFlightTransition, goToFirstStepHistory]);

  // No DialogTrigger means Radix has nothing to restore focus to when the
  // takeover closes, leaving it on <body>. Claim it on the remounted inline
  // step region instead (same fix as ThankYou.tsx's focus claim on
  // submit). Guarded on the open->closed transition so initial mount
  // doesn't steal focus.
  useEffect(() => {
    if (wasTakeoverOpenRef.current && !isTakeoverOpen) {
      inlineStepRegionRef.current?.focus({ preventScroll: true });
    }
    wasTakeoverOpenRef.current = isTakeoverOpen;
  }, [isTakeoverOpen]);

  return {
    captureFormElement,
    formElement,
    inlineStepRegionRef,
    isEnteringTakeover,
    isTakeoverOpen,
    onCloseTakeover,
  };
}

export { useLeadFormTakeover };
